import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading, Subheading } from '../../../../components/Heading';
import { Divider } from '../../../../components/Divider';
import { Input } from '../../../../components/Input';
import { Textarea } from '../../../../components/Textarea';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { PhotoIcon, CheckBadgeIcon } from '@heroicons/react/16/solid';
import Loading from '../../../../components/Loading';
import TrackingOrderService from '../../../../services/trackingOrderService';
import { TrackingOrder } from '../../../../models/TrackingOrder';
import dateFormat from 'dateformat';

const SubDeliveryManConfirmationPage = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [order, setOrder] = useState<TrackingOrder | null>(null);
    const [loading, setLoading] = useState(true);
    const [driverName, setDriverName] = useState('');
    const [vehicleNo, setVehicleNo] = useState('');
    const [notes, setNotes] = useState('');
    const [provenImage, setProvenImage] = useState<File | null>(null);
    const [fileName, setFileName] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = "Sub-Delivery Confirmation";
    }, []);

    useEffect(() => {
        const fetchOrder = async () => {
            if (!token) return;
            setLoading(true);
            try {
                const o = await TrackingOrderService.getBySubDeliveryManToken(token);
                setOrder(o);
            } catch (err) {
                showErrorToast('Order not found.');
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [token]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setProvenImage(file);
            setFileName(file.name);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!token) return;
        if (!driverName.trim()) {
            showErrorToast('Please enter driver name.');
            return;
        }
        if (!provenImage) {
            showErrorToast('Please upload a proof image.');
            return;
        }
        setSubmitting(true);
        try {
            showLoadingToast('Confirming sub-delivery...', 100);
            await TrackingOrderService.createSubDeliveryManConfirmation(
                token,
                { driver_name: driverName, vehicle_no: vehicleNo, notes },
                provenImage
            );
            showSuccessToast('Sub-delivery confirmed successfully.');
            navigate('/gp/tracking/standalone/success');
        } catch (err) {
            showErrorToast('Failed to confirm sub-delivery.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loading />;
    if (!order) return <p className="py-16 text-center text-red-700">Order not found.</p>;

    const subDeliveryMan = (order as any).sub_delivery_man;
    const alreadyConfirmed = subDeliveryMan && subDeliveryMan.confirmed_time;

    if (alreadyConfirmed) {
        return (
            <div className="py-12 mx-auto max-w-6xl px-4">
                <div className="flex items-center gap-2 text-green-600 mb-6">
                    <CheckBadgeIcon className="w-8 h-8" />
                    <Heading>Already Confirmed</Heading>
                </div>
                <p className="text-zinc-600 mb-8">This sub-delivery has already been confirmed.</p>
                <Divider className="mb-8" />
                <div className="space-y-6">
                    <div>
                        <Subheading>Reference No</Subheading>
                        <p className="mt-1">{order.reference_no}</p>
                    </div>
                    <div>
                        <Subheading>Driver Name</Subheading>
                        <p className="mt-1">{subDeliveryMan.driver_name || '-'}</p>
                    </div>
                    <div>
                        <Subheading>Vehicle No</Subheading>
                        <p className="mt-1">{subDeliveryMan.vehicle_no || '-'}</p>
                    </div>
                    <div>
                        <Subheading>Notes</Subheading>
                        <p className="mt-1">{subDeliveryMan.notes || '-'}</p>
                    </div>
                    <div>
                        <Subheading>Confirmed Time</Subheading>
                        <p className="mt-1">{dateFormat(subDeliveryMan.confirmed_time, "mmmm dd, yyyy hh:MM:ss")}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="py-12 mx-auto max-w-6xl px-4">
            <Heading>Sub-Delivery (Second Delivery Man) Confirmation</Heading>
            <Divider className="mt-5 mb-8" />
            <div className="space-y-12">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">Order Details</h2>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <Subheading>Reference No</Subheading>
                            <p className="mt-1">{order.reference_no}</p>
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Customer</Subheading>
                            <p className="mt-1">{order.customer_fullname || '-'}</p>
                        </div>
                        {order.items && order.items.length > 0 && (
                            <div className="sm:col-span-full">
                                <Subheading>Items</Subheading>
                                <div className="mt-2 overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                        <thead>
                                            <tr>
                                                <th className="py-2 pr-3 text-left font-semibold text-gray-900">Description</th>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-900">Qty</th>
                                                <th className="px-3 py-2 text-left font-semibold text-gray-900">UOM</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {order.items.map((item: any, idx: number) => (
                                                <tr key={idx}>
                                                    <td className="py-2 pr-3">{item.is_other ? item.description : (item.product_description || item.item_no || '-')}</td>
                                                    <td className="px-3 py-2">{item.quantity}</td>
                                                    <td className="px-3 py-2">{item.uom || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">Confirm Sub-Delivery</h2>
                        <p className="mt-1 text-sm leading-6 text-gray-600">Enter your details to confirm sub-delivery.</p>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <Subheading>Driver Name</Subheading>
                            <Input name="driver_name" value={driverName} onChange={(e) => setDriverName(e.target.value)} required />
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Vehicle Number</Subheading>
                            <Input name="vehicle_no" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder="e.g. ABC 1234" />
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Notes</Subheading>
                            <Textarea name="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Left at reception" rows={3} />
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Upload Proof Image</Subheading>
                            <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                                <div className="text-center">
                                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" />
                                    <div className="mt-4 flex text-sm leading-6 text-gray-600">
                                        <label htmlFor="proven_image" className="relative cursor-pointer rounded-md font-semibold text-primary hover:text-primary/80">
                                            <span>Upload a file</span>
                                            <input id="proven_image" name="proven_image" type="file" accept="image/png, image/jpeg" className="sr-only" onChange={handleFileChange} />
                                        </label>
                                        <p className="pl-1">of parcel delivered</p>
                                    </div>
                                    <p className="text-xs leading-5 text-gray-600">PNG, JPG up to 10MB</p>
                                </div>
                            </div>
                            {fileName && <p className="mt-2 text-sm text-gray-700">Selected: <strong>{fileName}</strong></p>}
                        </div>
                        <div className="sm:col-span-full">
                            <Button type="submit" disabled={submitting || !driverName.trim() || !provenImage}>
                                {submitting ? 'Submitting...' : 'Confirm Sub-Delivery'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default SubDeliveryManConfirmationPage;
